import { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { SocketProvider } from './contexts/SocketContext';
import { UsageProvider } from './contexts/UsageContext';
import { Toaster } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import Docs from './pages/Docs';
import UserChats from './pages/UserChats';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import Auth from './pages/Auth';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import Plans from './pages/Plans';
import IDE from './pages/IDE';
import Arena from './pages/Arena';

const AUTH_URL = '/auth';

function LoadingScreen() {
  const { t } = useTranslation('common');

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <svg className="h-8 w-8 animate-spin text-muted-foreground" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="8" cy="8" r="7" stroke="currentColor" strokeOpacity="0.25" strokeWidth="2" vectorEffect="non-scaling-stroke" />
          <path d="M15 8a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
        </svg>
        <span>{t('loading')}</span>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(window.location.href)}`;
    return null;
  }

  return children;
}

function AdminRoute({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    window.location.href = `${AUTH_URL}?redirect=${encodeURIComponent(window.location.href)}`;
    return null;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/console" replace />;
  }

  return children;
}

function AppContent() {
  const { loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route path="/docs/*" element={<Docs />} />
      <Route path="/console/security" element={<Navigate to="/console/account" replace />} />
      <Route path="/console/data" element={<Navigate to="/console" replace />} />
      <Route path="/console/*" element={<RequireAuth><Settings /></RequireAuth>} />
      <Route path="/ide" element={<Navigate to="/code" replace />} />
      <Route path="/ide/:workspaceId" element={<RequireAuth><IDE /></RequireAuth>} />
      <Route path="/code" element={<RequireAuth><IDE /></RequireAuth>} />
      <Route path="/code/:workspaceId" element={<RequireAuth><IDE /></RequireAuth>} />
      <Route path="/arena" element={<RequireAuth><Arena /></RequireAuth>} />
      <Route path="/dashboard/*" element={<AdminRoute><Dashboard /></AdminRoute>} />
      <Route path="/chats" element={<RequireAuth><UserChats /></RequireAuth>} />
      <Route path="/payment" element={<Navigate to="/console" replace />} />
      <Route path="/plans" element={<RequireAuth><Plans /></RequireAuth>} />
      <Route path="/settings/*" element={<Navigate to="/console" replace />} />
      <Route path="*" element={<Navigate to="/console" replace />} />
    </Routes>
  );
}

function AppWithTheme() {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const rawTheme = user?.preferences?.theme;
  const userTheme = rawTheme === 'dark' || rawTheme === 'light' ? rawTheme : 'system';

  const langSyncedRef = useRef(false);
  useEffect(() => {
    const lang = user?.preferences?.language;
    if (lang && !langSyncedRef.current) {
      langSyncedRef.current = true;
      localStorage.setItem('etherworks-language', lang);
      if (lang !== i18n.language) {
        i18n.changeLanguage(lang);
      }
    }
    if (!user) {
      langSyncedRef.current = false;
    }
  }, [user?.preferences?.language, i18n]);

  return (
    <ThemeProvider userTheme={userTheme}>
      <BrowserRouter>
        <AppContent />
          <Toaster
            position="top-center"
            toastOptions={{
              duration: 3000,
              style: {
                background: 'hsl(var(--card))',
                color: 'hsl(var(--foreground))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                boxShadow: '0 8px 24px rgb(15 23 42 / 0.10)',
                fontSize: '13px',
                fontWeight: 500,
                padding: '10px 14px',
                letterSpacing: '0',
              },
              success: { iconTheme: { primary: '#188038', secondary: 'white' } },
              error: { iconTheme: { primary: '#d93025', secondary: 'white' } },
            }}
          />
        </BrowserRouter>
    </ThemeProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <SocketProvider>
        <UsageProvider>
          <AppWithTheme />
        </UsageProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default App;
